/**
 * Trial Welcome Modal
 *
 * Modal informativo e convidativo exibido uma única vez no início do período de
 * degustação (após as configurações iniciais e antes do tutorial). Comunica que
 * o usuário tem X dias grátis com todos os recursos premium.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
import { differenceInCalendarDays } from "date-fns";
import { toBrasiliaWallClock } from '@/lib/brasiliaDate';

interface TrialWelcomeModalProps {
  open: boolean;
  trialEnd: string | null;
  onClose: () => void;
}

export function TrialWelcomeModal({ open, trialEnd, onClose }: Readonly<TrialWelcomeModalProps>) {
  // Dias pelo calendário de Brasília (trial_end vem em UTC sem fuso).
  const trialEndDay = toBrasiliaWallClock(trialEnd);
  const today = toBrasiliaWallClock(new Date());
  const daysRemaining =
    trialEndDay && today ? Math.max(0, differenceInCalendarDays(trialEndDay, today)) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg text-center">
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Seu período Premium começou!
          </DialogTitle>
          <DialogDescription className="text-base">
            Pelos próximos {daysRemaining ?? 7}{" "}
            {daysRemaining === 1 ? "dia" : "dias"}, você tem{" "}
            <strong>acesso livre e gratuito</strong> a todas as ferramentas avançadas do
            Librattum. Monte seu planejamento, antecipe suas despesas e descubra a
            tranquilidade de olhar para frente.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Button className="w-full py-6 text-base font-semibold" onClick={onClose}>
            Começar a usar o Librattum
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TrialWelcomeModal;
